// Resolves gamepad button presses into action IDs, mirroring what
// matchAction() in keybindings.js does for keyboard events.
//
// The effective binding for each action is `override ?? default`:
//   - Defaults come from KEYBINDING_ACTIONS.defaultGamepad.
//   - Overrides are user-set in the settings UI and persist via
//     persistentPrefs under STORAGE_KEY.
// `matchActionFromGamepad()` is a reverse lookup by button index, so
// it has to rebuild whenever overrides change. The table is
// invalidated on every setGamepadBinding / clearGamepadBinding.

import { KEYBINDING_ACTIONS } from '../game/keybindings';
import { getPref, setPref } from '../persistentPrefs';

const STORAGE_KEY = 'vk-gamepad-bindings';

function loadOverrides() {
  const stored = getPref(STORAGE_KEY, null);
  if (stored && typeof stored === 'object') return { ...stored };
  return {};
}

let overrides = null;
function ensureLoaded() {
  if (overrides === null) overrides = loadOverrides();
  return overrides;
}

function effectiveBindingFor(action) {
  const o = ensureLoaded();
  if (Object.prototype.hasOwnProperty.call(o, action.id)) return o[action.id];
  return action.defaultGamepad || null;
}

function buildTable() {
  // Map<buttonIndex, { actionId, hold }>. Multiple actions on the
  // same button are not expected at default; if a user-set override
  // collides with another action's binding the settings UI is
  // responsible for preventing that (it surfaces conflicts the way
  // keyboard rebinding already does). First-writer-wins if it sneaks
  // through.
  const table = new Map();
  for (const action of KEYBINDING_ACTIONS) {
    const gp = effectiveBindingFor(action);
    if (!gp || typeof gp.button !== 'number') continue;
    if (table.has(gp.button)) continue;
    table.set(gp.button, { actionId: action.id, hold: Boolean(gp.hold) });
  }
  return table;
}

let cachedTable = null;
function getTable() {
  if (!cachedTable) cachedTable = buildTable();
  return cachedTable;
}

function invalidate() {
  cachedTable = null;
}

export function matchActionFromGamepad(buttonIndex) {
  const hit = getTable().get(buttonIndex);
  return hit ? hit.actionId : null;
}

export function isHoldAction(buttonIndex) {
  const hit = getTable().get(buttonIndex);
  return Boolean(hit && hit.hold);
}

// Returns the currently-effective binding for an action, or null if
// unbound. Consumers (settings UI, help overlay) should use this
// instead of reading KEYBINDING_ACTIONS directly so they see user
// overrides.
export function getGamepadBinding(actionId) {
  const action = KEYBINDING_ACTIONS.find((a) => a.id === actionId);
  if (!action) return null;
  return effectiveBindingFor(action);
}

// Returns the effective button index for an action, or null if
// unbound. Used by the ButtonGlyph component and the rebind display.
export function getGamepadButton(actionId) {
  const b = getGamepadBinding(actionId);
  return b && typeof b.button === 'number' ? b.button : null;
}

// Persist an override. Pass `null` to mark the action explicitly
// unbound (different from reverting to default. Use resetGamepadBinding
// for that).
export function setGamepadBinding(actionId, binding) {
  if (!KEYBINDING_ACTIONS.some((a) => a.id === actionId)) return;
  const o = ensureLoaded();
  if (binding === null) {
    o[actionId] = null;
  } else if (binding && typeof binding.button === 'number') {
    o[actionId] = { button: binding.button, ...(binding.hold ? { hold: true } : {}) };
  } else {
    return;
  }
  setPref(STORAGE_KEY, o);
  invalidate();
}

// Revert an override so the action falls back to its default binding.
export function resetGamepadBinding(actionId) {
  const o = ensureLoaded();
  if (!Object.prototype.hasOwnProperty.call(o, actionId)) return;
  delete o[actionId];
  setPref(STORAGE_KEY, o);
  invalidate();
}

// If `button` is already bound to a different action, return that
// action's id; otherwise return null. Used by the rebind UI to
// surface conflicts.
export function findActionForGamepadButton(buttonIndex, { exclude } = {}) {
  for (const action of KEYBINDING_ACTIONS) {
    if (exclude && action.id === exclude) continue;
    const gp = effectiveBindingFor(action);
    if (gp && gp.button === buttonIndex) return action.id;
  }
  return null;
}

// Test hook. Lets unit tests rebuild the table after fiddling with
// overrides or the catalogue.
export function _resetForTest() {
  overrides = null;
  cachedTable = null;
}
